import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { TAG_TYPES } from '../constants/queryTags';
import { BaseApiUrls } from './ApiHelpers';
import baseQueryWithTimeout from './baseQueryWithTimeout';
import { getWelcomeDashboardUrl } from '../helpers/degradeApiHelper';

const { ACCESS_TOKEN } = TAG_TYPES;

export const customerAuth0Api = createApi({
  reducerPath: 'customerAuth0Api',
  baseQuery: fetchBaseQuery({
    baseUrl: BaseApiUrls.middlewareApiUrl
  }),
  tagTypes: [ACCESS_TOKEN],
  endpoints: (builder) => ({
    getCspAccessToken: builder.query({
      query: (auth0Token) => {
        return {
          url: '/api/customer/createcustomer',
          method: 'POST',
          headers: {
            Authorization: `Bearer ${auth0Token}`
          }
        };
      },
      providesTags: [ACCESS_TOKEN]
    }),
    updateUser: builder.mutation({
      query: ({ auth0Token, data }) => {
        return {
          url: '/api/customer/auth/updateuser',
          method: 'POST',
          headers: {
            Authorization: auth0Token
          },
          body: data
        };
      }
    }),
    verifyUser: builder.mutation({
      query: ({ auth0Token, data }) => {
        return {
          url: '/api/customer/auth/verifyuser',
          method: 'POST',
          headers: {
            Authorization: auth0Token
          },
          body: data
        };
      }
    }),
    confirmEnrollment: builder.mutation({
      query: ({ auth0Token, data }) => {
        return {
          url: '/api/customer/auth/confirmEnroll',
          method: 'POST',
          headers: {
            Authorization: auth0Token
          },
          body: data
        };
      }
    }),
    getWelcomeDashboard: builder.query({
      query: ({ auth0Token, isFallback, contactId }) => ({
        url: getWelcomeDashboardUrl(isFallback, contactId),
        headers: {
          Authorization: `Bearer ${auth0Token}`
        }
      })
    }),
    createPortalUserToken: builder.mutation({
      query: ({ auth0Token, customerToken }) => ({
        url: `/api/customer/createportalusertoken`,
        method: 'POST',
        headers: {
          Authorization: auth0Token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(customerToken)
      })
    }),
    acceptFirstLoginExperience: builder.mutation({
      query: ({ data, auth0Token, isFallback, contactId }) => ({
        url: getWelcomeDashboardUrl(isFallback, contactId),
        method: 'POST',
        headers: {
          Authorization: `Bearer ${auth0Token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })
    })
  })
});

export const customerApi = createApi({
  reducerPath: 'customerApi',
  baseQuery: baseQueryWithTimeout,
  endpoints: (builder) => ({
    getSearchAddress: builder.mutation({
      query: (data) => ({
        url: '/api/customer/searchaddress',
        method: 'POST',
        body: { AddressLine: data }
      })
    }),
    getCustomerDetail: builder.query({
      query: (auth0Token) => ({
        url: '/api/v3/customer/customerdetail',
        headers: { customAuthToken: auth0Token }
      })
    }),
    updateAddress: builder.mutation({
      query: (data) => ({
        url: '/api/customer/address',
        method: 'POST',
        body: data
      })
    }),
    updatePassword: builder.mutation({
      query: (data) => ({
        url: '/api/customer/auth/changePassword',
        method: 'POST',
        body: data
      })
    }),
    getFormatAddress: builder.query({
      query: (data) => ({
        url: `/api/customer/formatAddress/${data}`
      })
    }),
    updateAllReimbursementInformation: builder.mutation({
      query: ({ data, isPetsureUnderwriter }) => ({
        url: `/api/customer/ReimbursementInformation/${isPetsureUnderwriter}`,
        method: 'POST',
        body: data
      })
    }),
    updateAllPremiumPaymentDetails: builder.mutation({
      query: ({ data, isPetsureUnderwriter }) => ({
        url: `/api/customer/BankingInformation/${isPetsureUnderwriter}`,
        method: 'POST',
        body: data
      })
    }),
    validateStateWithBankingInfo: builder.mutation({
      query: (data) => ({
        url: '/api/Customer/validateStateWithBankingInfo',
        method: 'POST',
        body: data
      })
    })
  })
});

export const {
  useGetCspAccessTokenQuery,
  useUpdateUserMutation,
  useVerifyUserMutation,
  useConfirmEnrollmentMutation,
  useGetWelcomeDashboardQuery,
  useCreatePortalUserTokenMutation,
  useAcceptFirstLoginExperienceMutation
} = customerAuth0Api;

export const {
  useGetSearchAddressMutation,
  useUpdateAddressMutation,
  useUpdatePasswordMutation,
  useLazyGetCustomerDetailQuery,
  useGetFormatAddressQuery,
  useUpdateAllReimbursementInformationMutation,
  useUpdateAllPremiumPaymentDetailsMutation,
  useValidateStateWithBankingInfoMutation
} = customerApi;
